import Promise from 'bluebird';
import * as utilities from '@bingads-webui-campaign-legacy/editorial-appeal/utilities';
import { AppealAllForParentEntity, AppealByIDsForParentEntity } from './params';

export { isAppealableStatus } from '@bingads-webui-campaign-legacy/editorial-appeal/utilities';

const toType = obj =>
  ({})
    .toString.call(obj) // returns "[object Type]"
    .substring(8, obj.length - 1); // returns "Type"

const assertInputs = (appealParams) => {
  if (!appealParams) {
    throw new Error('appealParams is required');
  }
  if (!(appealParams instanceof AppealByIDsForParentEntity) && !(appealParams instanceof AppealAllForParentEntity)) {
    // TODO: see TODOs
    throw new Error(`appealParams must be an instance of  AppealByIDsForParentEntity, AppealAllForParentEntity. ${toType(appealParams)} is not supported`);
  }
};

const processRejectReasonsFromOdata = (controller, permissions) =>
  odataResponse => (odataResponse.Errors ?
    Promise.reject(odataResponse.Errors) :
    utilities.processRejectReasonsFromOdata(odataResponse, controller, permissions));

/**
 * @typedef {Object} AggregatedRejectReasons
 * @property {Array<number>} appealIds array of entity IDs that can be appealed
 * @property {Array<AppealData>} appealData array of appeal data
 * @property {boolean} appealData.Appeal whether the entity has been appealed(?)
 * @property {string} appealData.Justification justification for appeal
 * @property {boolean} appealData.IsAppealable whether the entity can be appealed
 * @property {Array<string>} appealData.RejectedReasons array of rejection reasons
 * @property {Array<string>} appealData.RejectedTerms array of rejection terms
 * @property {string} appealData.EntityType entity type
 */

const resolveAppealParams = (appealParams) => {
  const {
    getEditorialReasonsByIDsForParentEntity, getAllEntitiesForParentEntity,
  } = appealParams.options;
  let getEditorialReasons;
  let appealParamsPromise;
  if (appealParams instanceof AppealByIDsForParentEntity ||
    appealParams instanceof AppealAllForParentEntity) {
    getEditorialReasons = getEditorialReasonsByIDsForParentEntity;

    if (appealParams instanceof AppealAllForParentEntity) {
      appealParamsPromise = getAllEntitiesForParentEntity(appealParams)
        .then(appealParams.createAppealParamsWithIDs.bind(appealParams));
    } else {
      appealParamsPromise = Promise.resolve(appealParams);
    }
  } // TODO: add support for AppealByIDs and AppealAllByFilter when needed

  return Promise.props({ getEditorialReasons, appealParams: appealParamsPromise });
};

/**
 * Fetches and aggregates editorial rejections for given entities
 * @param {AppealByIDsForParentEntity|AppealAllForParentEntity|AppealByIDs|AppealAllByFilter} appealParams Customer ID
 * @returns {Promise<AggregatedRejectReasons>} a promise that resolves to an object of aggregated reject reasons
 */
const getAppealRejections = ({ appealParams, getEditorialReasons }) => {
  const {
    permissions,
    controller,
  } = appealParams.options;

  return Promise.resolve(appealParams)
    .then(getEditorialReasons)
    .then(processRejectReasonsFromOdata(controller, permissions));
};

export const editorialAppealAggregator = (appealParams) => {
  assertInputs(appealParams);
  return Promise.resolve(appealParams)
    .then(resolveAppealParams)
    .then(getAppealRejections);
};

export const requestAppeal = (appealParams, appealData, appealIds) => {
  const {
    requestAppealAllForParentEntity,
    requestAppealByIDsForParentEntity,
  } = appealParams.options;
  let requestAppealFunc;
  if (appealParams instanceof AppealByIDsForParentEntity) {
    requestAppealFunc = requestAppealByIDsForParentEntity;
  } else if (appealParams instanceof AppealAllForParentEntity) {
    requestAppealFunc = requestAppealAllForParentEntity;
  } // TODO: add support for AppealByIDs and AppealAllByFilter when needed

  return requestAppealFunc(appealParams, appealData, appealIds);
};
